import { selectCompanyBranding } from "app/modules/system/whiteLabelling/CompanyBrandingSlice";
import { useSelector } from "react-redux";

const AccessibilityPage: React.FC = () => {
  const companyName = useSelector(selectCompanyBranding)?.companyName;

  return (
    <>
      <div className="container d-flex flex-column p-5">
        <h3 className="mb-5">CPL Portal Accessibility Statement</h3>
        <p>This accessibility statement applies to the CPL Portal.</p>
        <p>
          This website is run by CPL. The {companyName} instance and created content is managed by {companyName}. We
          want as many people as possible to be able to use this website. For example, that means you should be able to:
        </p>
        <ul>
          <li>change colours, contrast levels and fonts</li>
          <li>zoom in up to 200% without the text spilling off the screen</li>
          <li>navigate most of the website using just a keyboard</li>
          <li>navigate most of the website using speech recognition software</li>
          <li>
            listen to most of the website using a screen reader (including the most recent versions of JAWS, NVDA and
            VoiceOver)
          </li>
        </ul>
        <p>
          We've also made the website text as simple as possible to understand.{" "}
          <a href="https://mcmw.abilitynet.org.uk/" data-test-id="accessibility-statement__my-computer-my-way-link">
            My Computer My Way
          </a>{" "}
          has advice on making your device easier to use if you have a disability and the{" "}
          <a href="https://www.ahead.ie/The-AT-Hive" data-test-id="accessibility-statement__at-hive-link">
            AT Hive
          </a>{" "}
          can help you find assistive technologies that work for you.
        </p>

        <h4>How accessible this website is</h4>
        <p>
          We know some parts of this website are not fully accessible. You can see a full list of any issues we
          currently know about in the Non-accessible content section of this statement.
        </p>
        <h4>Feedback and contact information</h4>
        <p>
          If you find any problems not listed on this page or think we're not meeting accessibility requirements,
          contact:{" "}
          <a href="mailto:accessibility@cplsoftware.com" data-test-id="accessibility-statement__contact-email-link">
            accessibility@cplsoftware.com
          </a>
        </p>
        <p>
          If you need information on this website in a different format like accessible PDF, large print, easy read,
          audio recording or braille, please contact your Property Manager.
        </p>
        <a
          href="http://www.w3.org/WAI/users/inaccessible"
          data-test-id="accessibility-statement__inaccessible-websites-tips-link"
        >
          Read tips on contacting organisations about inaccessible websites.
        </a>

        <h4>Non accessible content</h4>
        <ul>
          <li>
            Some icon images do not have appropriate alt text or accessible text names. This fails WCAG 1.1.1 Non-text
            Content (A).
          </li>
          <li>
            The dashboard contains visible section headings that are not also programmatic headings, there are some
            incorrectly constructed tables using generic elements that do not preserve table context and some
            disconnected input fields and label pairs across pages. This fails WCAG 1.3.1 Info and Relationships (A).
          </li>
          <li>
            Colour alone is used to differentiate some links or other information across the platform. This fails WCAG
            1.4.1 Use of Color (A).
          </li>
          <li>
            A small amount of text across the platform does not meet contrast requirements, this includes error messages
            on the sign in or register process, the FAQ link text, and contact information text in the payment journey.
            This fails WCAG 1.4.3 Contrast (Minimum) (AA).
          </li>
          <li>
            Many pages lose content at higher magnifications including table ordering and filter options as well as many
            interactive buttons which allow users to see more case, job and other information or download documents.
            This fails WCAG 1.4.10 Reflow (AA).
          </li>
          <li>
            Some navigational element focus indications or icons, and specific dark purple buttons on pages do not meet
            contrast requirements. This fails WCAG 1.4.11 Non-text Contrast (AA).
          </li>
          <li>
            A small number of pages do not respond to user-triggered text spacing adjustments. This fails WCAG 1.4.12
            Text Spacing (AA).
          </li>
          <li>
            A majority of content across the platform is not accessible to keyboard users including most controls in
            main content and important navigation elements. This fails WCAG 2.1.1 Keyboard (A).
          </li>
          <li>There is no skip to content function on the platform. This fails WCAG 2.4.1 Bypass Blocks (A).</li>
          <li>Pages do not have unique page titles. This fails WCAG 2.4.2 Page Titled (A).</li>
          <li>
            The Get in touch link in the footer popup is broken. This fails WCAG 2.4.4 Link Purpose (In Context) (A).
          </li>
          <li>
            Many elements across the platform including the cookies close button, close buttons for other popups, some
            contact links, news listings, pagination controls, and some filter options do not receive visible focus
            indication. This fails WCAG 2.4.7 Focus Visible (AA).
          </li>
          <li>
            Navigation content and other popups including the payment journey significantly overlaps and obscures main
            page content at higher magnifications but does not trap user focus. This fails WCAG 2.4.11 Focus Not
            Obscured (Minimum) (AA).
          </li>
          <li>
            Some table search input fields have accessible text names that differ to their visual label. This fails WCAG
            2.5.3 Label in Name (A).
          </li>
          <li>
            A large number of interactive controls and input fields do not use standard html elements which creates a
            significant number of naming, state or other functionality issues. This fails WCAG 4.1.2 Name, Role, Value
            (A).
          </li>
          <li>
            Once an account number has been submitted, there is a visual notification to let users know that the account
            has been linked successfully. This is not announced to screen reader users. This fails WCAG 4.1.3 Status
            Messages (AA).
          </li>
        </ul>
        <p>
          If you find an issue that we have yet to identify, please contact us using one of the routes described in the
          ‘Feedback and contact information’ section of this statement.
        </p>

        <h4>What we’re doing to improve accessibility</h4>
        <p>CPL is doing the following to improve accessibility…</p>

        <h5>Preparation of this accessibility statement</h5>
        <p>This statement was prepared on 01/04/2025. It was last reviewed on 01/04/2025.</p>
        <p>
          This website was last tested on 10/03/2025 against the WCAG 2.2 standard. The test was carried out by{" "}
          <a href="https://www.allable.co.uk/" data-test-id="accessibility-statement__all-able-link">
            All Able Ltd
          </a>
          .
        </p>
        <p>
          A representative sample of pages of the website were tested along with a sample of the documents from each
          area of the website. Testing was composed of a mixture of manual, semi-automated and assistive technology
          testing.
        </p>
      </div>
    </>
  );
};

export default AccessibilityPage;
